import React, { useEffect, useState } from "react";
import { Select, Spin } from "antd";
import { Icon } from "Components";
import Alert from "../../components/Alert";
import StepFooter from "../../components/StepFooter";
import PropertyRecipients from "./PropertyRecipients";
import OrganizationRecipients from "./OrganizationRecipients";
import GroupRecipients from "./GroupRecipients";
import IndividualRecipients from "./IndividualRecipients";
import { getAllProperties, getOrganizationUsers, submitStep3, submitStep3Org, submitStep3Prop } from "PhishingManager/service/index.Service";
import { errorMessageHandler, getGroupsPayload } from "PhishingManager/utils";
import { GroupOption, PropertyState, RoleOption } from "./Step3.types";
import RolesRecipients from "./RolesRecipients";

type StepProps = {
  tenantId: number | string | undefined;
  campaignId: string | number | null;
  onBack: () => void;
  onNext: () => void;
  isMobile: boolean;
  isActive: boolean;
  setToast: (toast: { message: string; type: 'warning' | 'success' }) => void;
};

type SelectionMethod =
  | "organization"
  | "property"
  | "role"
  | "group"
  | "individual"
  | "";

interface SelectorOption {
  key: SelectionMethod;
  icon: React.ReactNode;
  label: string;
  description: string;
}

const selectionOptions: SelectorOption[] = [
  {
    key: "organization",
    icon: <Icon type="recipientBuilding" />,
    label: "By Organization",
    description: "Send to all users in your organization",
  },
  {
    key: "property",
    icon: <Icon type="recipientProperty" />,
    label: "By Property",
    description: "Select entire properties",
  },
  {
    key: "role",
    icon: <Icon type="recipientRole" />,
    label: "By Role",
    description: "Send to all users assigned to a specific role",
  },
  {
    key: "group",
    icon: <Icon type="recipientGroup" />,
    label: "By Group",
    description: "Select groups or departments",
  },
  {
    key: "individual",
    icon: <Icon type="recipientIndividual" />,
    label: "By Individual",
    description: "Select specific individuals by name or email",
  },
];

const roleOptions = [
  {
    key: "staff",
    label: "Staff",
    count: 40,
    description: "All users other than Managers and Leadership team",
  },
  {
    key: "manager",
    label: "Manager",
    count: 5,
    description: "Department and property managers",
  },
  {
    key: "leadership",
    label: "Leadership and IT",
    count: 2,
    description: "Those overseeing system and strategy",
  },
];

const propertyOptions = [
  { value: "premier-inn", label: "Premier Inn", count: 520 },
  { value: "plaza-hotel", label: "Plaza Hotel", count: 480 },
  { value: "royal-sunset", label: "Royal Sunset Hotel", count: 80 },
  { value: "central-business", label: "Central Business Hotel", count: 80 },
  { value: "grand-palace", label: "Grand Palace Resort", count: 410 },
  { value: "premier-inn2", label: "Premier Inn2", count: 520 },
  { value: "plaza-hotel2", label: "Plaza Hotel", count: 480 },
  { value: "royal-sunset2", label: "Royal Sunset Hotel2", count: 80 },
  { value: "central-business2", label: "Central Business Hotel2", count: 80 },
  { value: "grand-palace2", label: "Grand Palace Resort2", count: 410 },
];

const propertyRoles: Record<string, { key: string; count: number }[]> = {
  "premier-inn": [{ key: "staff", count: 430 }, { key: "manager", count: 70 }, { key: "leadership", count: 20 }],
  "plaza-hotel": [{ key: "staff", count: 390 }, { key: "manager", count: 70 }, { key: "leadership", count: 20 }],
  "royal-sunset": [{ key: "manager", count: 80 }],
  "central-business": [{ key: "manager", count: 60 }, { key: "leadership", count: 20 }],
  "grand-palace": [{ key: "staff", count: 390 }, { key: "leadership", count: 20 }],
};

const individualOptions = [
  { key: "alice-johnson", label: "Alice Johnson", email: "ajohnson@gplaza.com", propertyKey: "plaza-hotel" },
  { key: "benjamin-smith", label: "Benjamin Smith", email: "bsmith@gplaza.com", propertyKey: "plaza-hotel" },
  { key: "charlotte-brown", label: "Charlotte Brown", email: "cbrown@marriott.com", propertyKey: "premier-inn" },
  { key: "david-wilson", label: "David Wilson", email: "dwilson@marriott.com", propertyKey: "premier-inn" },
  { key: "emily-davis", label: "Emily Davis", email: "edavis@marriott.com", propertyKey: "premier-inn" },
  { key: "frank-miller", label: "Frank Miller", email: "fmiller@marriott.com", propertyKey: "premier-inn" },
  { key: "grace-lee", label: "Grace Lee", email: "glee@marriott.com", propertyKey: "royal-sunset" },
  { key: "johnson2", label: "Johnson Jr", email: "ajohnson@gplaza.com", propertyKey: "plaza-hotel" },
  { key: "benjamin-smith2", label: "Benjamin Smith Jr", email: "bsmith@gplaza.com", propertyKey: "plaza-hotel" },
  { key: "charlotte-brown2", label: "Charlotte Brown Jr", email: "cbrown@marriott.com", propertyKey: "premier-inn" },
  { key: "david-wilson2", label: "David Wilson Jr", email: "dwilson@marriott.com", propertyKey: "premier-inn" },
  { key: "emily-davis2", label: "Emily Davis Jr", email: "edavis@marriott.com", propertyKey: "premier-inn" },
  { key: "frank-miller2", label: "Frank Miller Jr", email: "fmiller@marriott.com", propertyKey: "premier-inn" },
  { key: "grace-lee2", label: "Grace Lee Jr", email: "glee@marriott.com", propertyKey: "royal-sunset" }
];

const initState = {
  search: "",
  data: [],
  currentPage: 1,
  totalPages: 0
}

const Step3Recipients: React.FC<StepProps> = ({ onBack, onNext, isMobile, campaignId, tenantId, setToast, isActive }) => {
  const [selectedMethod, setSelectedMethod] = useState<SelectionMethod>("");
  const [organizationUsers, setOrganizationUsers] = useState(0);
  const [organizationSelected, setOrganizationSelected] = useState(false);
  const [selectedProperties, setSelectedProperties] = useState<number[]>([]);
  const [selectedRoles, setSelectedRoles] = useState<RoleOption[]>([]);
  const [selectedGroups, setSelectedGroups] = useState<GroupOption[]>([]);
  const [selectedIndividuals, setSelectedIndividuals] = useState<string[]>([]);
  const [individualSearchQuery, setIndividualSearchQuery] = useState("");
  const [propertyState, setPropertyState] = useState<PropertyState>({ ...initState });
  const [allPropertiesSelected, setAllPropertiesSelected] = useState(false);
  const [loading, setLoading] = useState(false);
  const [propertiesLoading, setPropertiesLoading] = useState(false);
  const [requestDone, setRequestDone] = useState(false);

  const isNextDisabled = !selectedMethod
    ? true
    : selectedMethod === "organization"
      ? !organizationSelected
      : selectedMethod === "role"
        ? selectedRoles.length === 0
        : selectedMethod === "property"
          ? selectedProperties.length === 0
          : selectedMethod === "group"
            ? selectedGroups.length === 0
            : selectedMethod === "individual"
              ? true
              : false;

  const handleMethodChange = (method: SelectionMethod) => {
    setSelectedMethod(method);
    setOrganizationSelected(false);
    setSelectedRoles([]);
    setSelectedProperties([]);
    setSelectedGroups([]);
    setSelectedIndividuals([]);
    setIndividualSearchQuery("");
    if (propertyState.search) setPropertyState(prev => ({ ...prev, search: "" }));
  };

  const handleSubmit = async () => {
    if (!campaignId || !tenantId || isNextDisabled) return;

    try {
      if (selectedMethod) {
        setLoading(true);
        let res = null;
        const payload: any = { campaign_id: Number(campaignId), venza_tenant_id: Number(tenantId) }
        if (selectedMethod == "organization") {
          res = await submitStep3Org(payload);
        } else if (selectedMethod === "property") {
          if (!selectedProperties.length) throw new Error("Please select a property");
          payload["property_ids"] = selectedProperties;
          res = await submitStep3Prop(payload);
        } else if (selectedMethod === "group" || selectedMethod === "role") {
          const list = selectedMethod === "group" ? selectedGroups : selectedRoles;
          payload["properties"] = getGroupsPayload(list, selectedMethod);
          res = await submitStep3(selectedMethod, payload);
        }

        if (res?.status === 'success') onNext();
      }
    } catch (err: any) {
      const errorMessage = errorMessageHandler(err, 'Failed to create campaign step 3');
      setToast({ message: errorMessage, type: 'warning' });
    } finally {
      setLoading(false);
    }
  };

  const getOrgUsers = async () => {
    try {
      if (!tenantId) return;
      setLoading(true);
      const res = await getOrganizationUsers(Number(tenantId));
      setOrganizationUsers(res?.data?.total_users || 0);
    } catch (error) {
      setOrganizationUsers(0);
    } finally {
      setLoading(false);
    }
  };

  const getProperties = async () => {
    try {
      if (!tenantId || loading || propertiesLoading) return;
      selectedMethod === "property" ? setLoading(true) : setPropertiesLoading(true);
      const res = await getAllProperties(Number(tenantId), propertyState.search, propertyState.currentPage, 25);
      const propertyData = (res?.data || []).map((item: any) => ({
        value: item.venza_group_id,
        label: item.name,
        count: item.total_users || 0,
      }));

      setPropertyState(prev => {
        const baseData = propertyState.currentPage === 1 ? propertyData : [...prev.data, ...propertyData];
        return {
          ...prev,
          data: baseData,
          totalPages: res?.meta?.total || 0
        };
      });
    } catch (error) {
      setPropertyState(prev => ({ ...prev, data: [], totalPages: 0, currentPage: 1 }));
    } finally {
      selectedMethod === "property" ? setLoading(false) : setPropertiesLoading(false);
    }
  };

  const setPropertySearchQuery = (value: string | null) => {
    const obj: any = { search: value || "" };
    if (value !== null && propertyState.search !== value) obj['currentPage'] = 1;
    setPropertyState(prev => ({ ...prev, ...obj }));
  }

  const moveCurrentPage = () => setPropertyState(prev => ({ ...prev, currentPage: prev.currentPage + 1 }));

  useEffect(() => {
    if (isActive && selectedMethod === "organization" && organizationUsers === 0) getOrgUsers();
  }, [isActive, organizationUsers, selectedMethod])

  useEffect(() => {
    if (isActive && selectedMethod && selectedMethod !== "organization") setRequestDone(true);
  }, [isActive, selectedMethod])

  useEffect(() => {
    if (requestDone) getProperties();
  }, [requestDone, propertyState.currentPage, propertyState.search])

  return (
    <>
      <div className="step-card phishing-step3">
        <div className="card-header">
          <div className="card-title">Full Campaign Recipients</div>
          <div className="card-subtitle">
            Select the recipients who will receive the full phishing campaign
            once the test is completed.
          </div>
        </div>

        <div className="phishing-step3__selection">
          <div className="phishing-step3__label">
            Selection method <span className="required">*</span>
          </div>

          {isMobile ? (
            <div className="phishing-step3__dropdown--mobile">
              <Select
                style={{ width: "100%" }}
                value={selectedMethod || undefined}
                dropdownClassName="campaign-setup-filter-option-select"
                onChange={(value) => handleMethodChange(value)}
                options={selectionOptions.map((opt) => ({
                  value: opt.key,
                  label: opt.label,
                }))}
                placeholder="Select a method"
                size="large"
                getPopupContainer={(trigger) => trigger.parentElement}
              />
            </div>
          ) : (
            <div className="phishing-step3__grid">
              {selectionOptions.map((opt) => {
                const active = selectedMethod === opt.key;
                return (
                  <div
                    key={opt.key}
                    onClick={() => handleMethodChange(opt.key)}
                    className={`phishing-step3__selector ${active ? "phishing-step3__selector--active" : ""
                      }`}
                  >
                    <div className="phishing-step3__selector-header">
                      <div className="phishing-step3__icon">{opt.icon}</div>
                      <span className="phishing-step3__selector-title">
                        {opt.label}
                      </span>
                    </div>
                    <span className="phishing-step3__selector-desc">
                      {opt.description}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <Spin spinning={loading}>
          {selectedMethod === "organization" && (
            <OrganizationRecipients
              organizationUsers={organizationUsers}
              organizationSelected={organizationSelected}
              setOrganizationSelected={setOrganizationSelected}
            />
          )}

          {selectedMethod === "property" && (
            <PropertyRecipients {...{ selectedProperties, moveCurrentPage, setSelectedProperties, setPropertySearchQuery }}
              propertyOptions={propertyState.data}
              totalProperties={propertyState.totalPages}
            />
          )}

          {selectedMethod === "role" && (
            <RolesRecipients {...{ selectedProperties, setSelectedProperties, moveCurrentPage, setPropertySearchQuery, allPropertiesSelected, setAllPropertiesSelected, selectedRoles, setSelectedRoles }}
              loading={propertiesLoading}
              tenentId={Number(tenantId)}
              propertyData={propertyState}
              toggleLoading={(status) => setLoading(status)}
          />)}

          {selectedMethod === "group" && (
            <GroupRecipients {...{ selectedGroups, selectedProperties, setSelectedProperties, moveCurrentPage, setPropertySearchQuery, allPropertiesSelected, setAllPropertiesSelected, setSelectedGroups }}
              loading={propertiesLoading}
              tenentId={Number(tenantId)}
              propertyData={propertyState}
              toggleLoading={(status) => setLoading(status)}
            />
          )}

          {selectedMethod === "individual" && (
            <IndividualRecipients
              propertyOptions={propertyOptions}
              individualOptions={individualOptions}
              selectedIndividuals={selectedIndividuals}
              setSelectedIndividuals={setSelectedIndividuals}
              individualSearchQuery={individualSearchQuery}
              setIndividualSearchQuery={setIndividualSearchQuery}
            />
          )}
        </Spin>

        <div className="alert-section">
          <Alert
            title="Information"
            subtitle="No emails will be sent to these recipients until the test is completed and the full campaign is released."
          />
        </div>
      </div>

      <StepFooter
        isLoading={!isNextDisabled && loading}
        onBack={onBack}
        onNext={handleSubmit}
        nextBtnDisabled={isNextDisabled}
      />
    </>
  );
};

export default Step3Recipients;